type OpenBlockPayload = {
  js?: string;
  lazy?: boolean;
};

type OpenBlockApi = {
  block_root: HTMLElement;

  block_query: <T extends Element = Element>(selector: string) => T | null;
  block_queryAll: <T extends Element = Element>(selector: string) => T[];

  block_listen: (el: Element | null | undefined, eventType: string, handler: EventListenerOrEventListenerObject, options?: AddEventListenerOptions | boolean) => void;

  block_requestFrame: (callback: FrameRequestCallback) => number;

  block_observeVisibility: (callback: IntersectionObserverCallback, options?: IntersectionObserverInit) => IntersectionObserver;

  block_cleanup: () => void;
};

export default function OpenBlockRuntime() {
  const blocks = document.querySelectorAll<HTMLElement>("[data-open-block]");

  if (!blocks || blocks.length === 0) return;

  blocks.forEach((block_root) => {
    // prevent double init (edit mode partial refresh / re-hydration scenarios)
    const isInit = block_root.getAttribute("data-open-block-init") === "true";
    const hasInitAt = block_root.hasAttribute("data-open-block-init-at");

    // Only skip if it *actually* finished initializing once
    if (isInit && hasInitAt) return;

    if (isInit && !hasInitAt) {
      console.warn("OpenBlock: init flag set but no init-at found; re-running.", block_root);
      block_root.removeAttribute("data-open-block-init");
    }

    const payloadScript = block_root.querySelector<HTMLScriptElement>("[data-open-block-js]");

    if (!payloadScript) return;

    const parsePayload = (): OpenBlockPayload | null => {
      try {
        const raw = payloadScript.textContent?.trim();
        if (!raw) return null;
        return JSON.parse(raw) as OpenBlockPayload;
      } catch (error) {
        console.error("OpenBlock: invalid JSON payload", error, block_root);
        return null;
      }
    };

    const payload = parsePayload();
    if (!payload?.js) return;

    const cleanups: Array<() => void> = [];

    const api: OpenBlockApi = {
      block_root,

      block_query: <T extends Element = Element>(selector: string) => block_root.querySelector<T>(selector),

      block_queryAll: <T extends Element = Element>(selector: string) => Array.from(block_root.querySelectorAll<T>(selector)),

      block_listen: (el, eventType, handler, options) => {
        if (!el) return;
        el.addEventListener(eventType, handler, options);
        cleanups.push(() => el.removeEventListener(eventType, handler, options));
      },

      block_requestFrame: (callback) => window.requestAnimationFrame(callback),

      block_observeVisibility: (callback, options) => {
        const observer = new IntersectionObserver(callback, options);
        observer.observe(block_root);
        cleanups.push(() => observer.disconnect());
        return observer;
      },

      block_cleanup: () => {
        while (cleanups.length > 0) {
          try {
            cleanups.pop()?.();
          } catch (error) {
            console.warn("OpenBlock: block_cleanup step failed (non-blocking).", error, block_root);
          }
        }
      },
    };

    const executeBlockScript = () => {
      // prevent double init (fast scroll/IO, re-entrancy)
      if (block_root.getAttribute("data-open-block-running") === "true") return;
      if (block_root.getAttribute("data-open-block-init") === "true") return;

      block_root.setAttribute("data-open-block-running", "true");

      try {
        const userScriptFunction = new Function("api", `"use strict";\n${payload.js}\n`);

        userScriptFunction(api);

        // mark initialized only after successful execution
        block_root.setAttribute("data-open-block-init", "true");
      } catch (error) {
        console.error("OpenBlock: JS execution error", error, block_root);
      } finally {
        block_root.removeAttribute("data-open-block-running");
      }
    };

    const lazy = payload.lazy === true;

    if (lazy && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((entry) => entry.isIntersecting);
          if (!isVisible) return;

          observer.disconnect();
          executeBlockScript();
        },
        { rootMargin: "200px 0px", threshold: 0.01 },
      );

      observer.observe(block_root);
      // no need to add to cleanups because we disconnect after first intersect
      return;
    }

    // no lazy, or IO unsupported
    executeBlockScript();
  });
}
